import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import { ChatbotService, ChatMessage } from '../../services/chatbot.service';

@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    DropdownModule
  ],
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.css']
})
export class ChatbotComponent implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  
  messages: ChatMessage[] = [];
  currentMessage: string = '';
  isLoading: boolean = false;
  isChatOpen: boolean = false;
  selectedDomain: string = 'emprunteur';
  
  domainOptions = [
    { label: 'Assurance Emprunteur', value: 'emprunteur', icon: 'pi pi-home' },
    { label: 'Assurance Santé', value: 'sante', icon: 'pi pi-heart' }
  ];

  quickSuggestions: string[] = [];
  
  emprunteurSuggestions = [
    'Quels sont les types d\'assurance emprunteur ?',
    'Comment calculer ma prime d\'assurance ?',
    'Puis-je changer d\'assurance en cours de prêt ?',
    'Quelles sont les garanties obligatoires ?'
  ];
  
  santeSuggestions = [
    'Quelles sont les garanties santé disponibles ?',
    'Comment fonctionne le remboursement des frais médicaux ?',
    'Quelle mutuelle choisir selon mon profil ?',
    'Quels sont les délais de carence ?'
  ];

  constructor(
    private chatbotService: ChatbotService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    this.updateSuggestionsAndWelcome();
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  toggleChat() {
    this.isChatOpen = !this.isChatOpen;
  }

  sendMessage() {
    if (!this.currentMessage.trim() || this.isLoading) return;
    
    this.processMessage(this.currentMessage.trim());
    this.currentMessage = '';
  }

  sendSuggestion(suggestion: string) {
    this.processMessage(suggestion);
  }

  private processMessage(message: string) {
    this.messages.push({
      content: message,
      isUser: true,
      timestamp: new Date()
    });

    this.isLoading = true;

    this.chatbotService.askQuestion(message, this.selectedDomain).subscribe({
      next: (response) => {
        this.messages.push({
          content: response.answer, // Utilise 'answer' du backend Flask
          isUser: false,
          timestamp: new Date()
        });
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erreur chatbot:', error);
        this.messages.push({
          content: error.message || 'Désolé, je rencontre un problème technique. Veuillez réessayer plus tard.',
          isUser: false,
          timestamp: new Date()
        });
        this.isLoading = false;
      }
    });
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  clearChat() {
    this.updateSuggestionsAndWelcome();
  }
  
  onDomainChange() {
    this.updateSuggestionsAndWelcome();
  }
  
  private updateSuggestionsAndWelcome() {
    // Mettre à jour les suggestions selon le domaine
    this.quickSuggestions = this.selectedDomain === 'emprunteur' 
      ? this.emprunteurSuggestions 
      : this.santeSuggestions;
    
    // Message de bienvenue adapté au domaine
    const domainName = this.selectedDomain === 'emprunteur' ? 'assurance emprunteur' : 'assurance santé';
    const welcomeMessage = `Bonjour ! Je suis votre assistant virtuel Brokins. Comment puis-je vous aider avec vos questions d'${domainName} aujourd'hui ?`;
    
    this.messages = [{
      content: welcomeMessage,
      isUser: false,
      timestamp: new Date()
    }];
  }

  // Getters pour éviter les expressions complexes dans le template
  get selectedDomainIcon(): string {
    return this.domainOptions.find(opt => opt.value === this.selectedDomain)?.icon || 'pi pi-home';
  }

  get selectedDomainLabel(): string {
    return this.domainOptions.find(opt => opt.value === this.selectedDomain)?.label || 'Assurance Emprunteur';
  }

  formatMessage(content: string): SafeHtml {
    if (!content) {
      return '';
    }

    let text = content;

    // Escape HTML special characters first to avoid injecting raw HTML
    text = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Convert basic markdown bold **text** to <strong>text</strong>
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1<\/strong>');

    // Split into lines to handle lists and paragraphs
    const lines = text.split(/\r?\n/);
    const htmlParts: string[] = [];
    let inList = false;

    for (const line of lines) {
      const listMatch = line.match(/^\s*[-*]\s+(.*)/);
      if (listMatch) {
        if (!inList) {
          htmlParts.push('<ul>');
          inList = true;
        }
        htmlParts.push(`<li>${listMatch[1]}</li>`);
      } else {
        if (inList) {
          htmlParts.push('</ul>');
          inList = false;
        }
        const trimmed = line.trim();
        if (trimmed) {
          htmlParts.push(`<p>${trimmed}</p>`);
        } else {
          // Empty line -> line break between paragraphs
          htmlParts.push('<br>');
        }
      }
    }

    if (inList) {
      htmlParts.push('</ul>');
    }

    const html = htmlParts.join('');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  copyMessage(content: string) {
    navigator.clipboard.writeText(content.replace(/<[^>]*>/g, '')).then(() => {
      console.log('Message copié !');
    });
  }

  private scrollToBottom() {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = 
          this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Erreur lors du scroll:', err);
    }
  }
}
